import { request } from 'api';
import { endpoints } from 'api/endpoints';
import { AxiosError, AxiosResponse } from 'axios';
import { FileSizePart, chatMonthValues, monthValues, monthsMap } from 'const';
import {
  addHours,
  compareAsc,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  parse,
  subDays,
  subHours,
} from 'date-fns';
import { ru } from 'date-fns/locale';
import buildLocalizeFn from 'date-fns/locale/_lib/buildLocalizeFn';
import { heicTo } from 'heic-to';
import { OrderFilters, OrderTabType } from 'pages/Orders/types';
import { AuthContextProps } from 'react-oidc-context';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from 'react-query';
import { MultiValue, SingleValue } from 'react-select';
import { toast } from 'react-toastify';
import {
  ClientPaymentRequestDto,
  LocalizedOrderReceiptStatusNameEnum,
  OrderDetailsStatusEnum,
} from 'services/AgentService/types';
import {
  BaseError,
  ErrorResponse,
  ExtendedSuggestion,
  FileContentDto,
  OrderByFilterRequest,
  OrderFileContent,
  SettlementShort,
  StaticFileDownloadType,
} from 'types';
import { ExecutorPaymentRequestDto } from 'types/orders';
import { StatusByType, StatusForOrderFilters, Statuses } from 'types/status';
import { OptionType } from 'ui/Select';
import { StaticFiles } from 'utils/settings';

export function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

const formatUTCDate = (date: Date, formatStr: string) => {
  const pad = (num: number) => num.toString().padStart(2, '0');

  const day = pad(date.getUTCDate());
  const month = pad(date.getUTCMonth() + 1);
  const year = pad(date.getUTCFullYear());
  const hours = pad(date.getUTCHours());
  const minutes = pad(date.getUTCMinutes());

  return formatStr
    .replace('dd', day)
    .replace('MM', month)
    .replace('yyyy', year)
    .replace('HH', hours)
    .replace('mm', minutes);
};

export const getDateWithTimezone = (date: string, timeZone?: number, frmt?: string) => {
  const format = frmt ?? 'dd.MM.yyyy HH:mm';
  const parsedDate = new Date(date);

  if (timeZone) {
    parsedDate.setUTCHours(parsedDate.getUTCHours() + timeZone);
  }

  return formatUTCDate(parsedDate, format);
};

export const getDateWithoutUTC = (date: string) => {
  return date.replace(/ \(UTC\+\d+\)/gi, '');
};

export const getFileType = (mime: string): string => {
  const fileMap: { [p: string]: string } = {
    'image/png': 'png',
    'image/jpeg': 'jpeg',
    'application/pdf': 'pdf',
  };
  return fileMap[mime];
};

export const transformToSelectOptions = <T>(
  array: T[] | undefined,
  keyLabel: keyof T,
  keyValue?: keyof T,
  actualClients?: any
) => {
  let result = Array.isArray(array)
    ? array.map((item) => ({
        label: item[keyLabel],
        value: keyValue ? item[keyValue] : item,
      }))
    : [];
  if (actualClients?.length > 0) {
    const valueSet: any = new Set(actualClients?.map((item: any) => item.value));
    result = result.filter((item) => !valueSet.has(item.value));
  }
  return result;
};

// https://github.com/date-fns/date-fns/issues/229#issuecomment-416190283
const formatDistance = (from: Date, to: Date) => {
  const result: {
    days: number;
    hours: number;
    minutes: number;
  } = { days: 0, hours: 0, minutes: 0 };
  const now = from;
  let future = to;

  const days = differenceInDays(future, now);
  result['days'] = Math.abs(days);
  future = subDays(future, days);

  const hours = differenceInHours(future, now);
  result['hours'] = Math.abs(hours);
  future = subHours(future, hours);

  const minutes = differenceInMinutes(future, now);
  result['minutes'] = Math.abs(minutes);

  return result;
};

export const formatOrderRemainingTime = (timeISO: string, timeZone?: number) => {
  if (timeISO) {
    const now = new Date();
    const UTC0 = subHours(new Date(), Math.abs(now.getTimezoneOffset() / 60));
    const currentUserUTCTimezoneDate = addHours(UTC0, timeZone || 0);
    const parsed = parse(
      getDateWithTimezone(timeISO, timeZone),
      'dd.MM.yyyy HH:mm',
      currentUserUTCTimezoneDate
    );
    const { days, hours, minutes } = formatDistance(parsed, currentUserUTCTimezoneDate);
    const compare = compareAsc(parsed, new Date());
    return `${compare < 0 ? '-' : ''}${days} д. ${hours} ч. ${minutes} м.`;
  }
  return '';
};

export const isVisibleInStatus = (statusGroup: keyof typeof StatusByType, statusId: number) => {
  return StatusByType[statusGroup].includes(statusId);
};

export const orderByFilterRequestSkeleton: OrderByFilterRequest = {
  pageNumber: 1,
  pageSize: 25,
  tab: 1,
  searchParameters: {
    creationDateTimeStart: null,
    creationDateTimeEnd: null,
    clientIds: [],
    cityFiasIds: [],
    customId: '',
    areDependentObjectsIncluded: null,
  },
};

const tabIdByName: { [k in OrderTabType]: number } = {
  new: 1,
  inProgress: 2,
  completed: 3,
};

export const prepareFilterData = (val: MultiValue<OptionType> | SingleValue<OptionType>) => {
  return val instanceof Array ? val.map((x) => x.value) : [val?.value];
};

export const getFilter = (
  type: OrderTabType = 'new',
  body?: OrderFilters,
  page = 1,
  pageSize = 250
) => {
  const _body = orderByFilterRequestSkeleton;
  _body.searchParameters = {
    creationDateTimeStart:
      body && body.create_date && body.create_date.startDate
        ? new Date(body.create_date.startDate).toISOString()
        : null,
    creationDateTimeEnd:
      body && body.create_date && body.create_date.endDate
        ? new Date(body.create_date.endDate).toISOString()
        : null,
    clientIds: body?.clients
      ? prepareFilterData(body?.clients as MultiValue<OptionType> | SingleValue<OptionType>)
      : [],
    customId: body?.customId ? body.customId : '',
    cityFiasIds: body?.cities
      ? prepareFilterData(body.cities as MultiValue<OptionType> | SingleValue<OptionType>)
      : [],
    areDependentObjectsIncluded: false,
  };
  _body.tab = tabIdByName[type];
  _body.pageSize = pageSize;
  _body.pageNumber = page;

  return _body;
};

export const getStatusesByType = (typeStatus: keyof typeof StatusByType) =>
  StatusForOrderFilters[typeStatus].statuses.map((id) => Statuses[id]);

export const fetchFile = async (hash: string): Promise<AxiosResponse<OrderFileContent>> => {
  return await request(endpoints.DOWNLOAD_FILE.type, endpoints.DOWNLOAD_FILE.url(hash));
};

export const deleteFile = async (hash: string) => {
  return await request(endpoints.DELETE_FILE.type, endpoints.DELETE_FILE.url(hash));
};

export const downloadStaticFile = async (type: StaticFiles) => {
  return await request<StaticFileDownloadType>(
    endpoints.STATIC_FILE_DOWNLOAD.type,
    endpoints.STATIC_FILE_DOWNLOAD.url(type)
  );
};

/** TODO Удалить после перехода на новое api файлов */
export const getFileUrl = (file: OrderFileContent) => {
  const fileType = getFileType(file.contentType);
  const chars = window.atob(file.content);
  const bytes = new Array(chars.length);
  for (let i = 0; i < chars.length; i++) {
    bytes[i] = chars.charCodeAt(i);
  }
  const blob = new Blob([new Uint8Array(bytes)], { type: fileType });
  return window.URL.createObjectURL(blob);
};

export const getFileUrlNew = (file: FileContentDto) => {
  if (file.contentType && file.content) {
    const fileType = getFileType(file.contentType);
    const chars = window.atob(file.content);
    const bytes = new Array(chars.length);
    for (let i = 0; i < chars.length; i++) {
      bytes[i] = chars.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(bytes)], { type: fileType });
    return window.URL.createObjectURL(blob);
  }
  return '';
};

/** TODO Удалить после перехода на новое api файлов */
export const handleFileDownload = async (callback: Promise<AxiosResponse<OrderFileContent>>) => {
  const response = await callback;
  const file = response.data;
  const url = getFileUrl(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.fileName;
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
};

export const handleFileDownloadNew = (file: FileContentDto) => {
  const url = getFileUrlNew(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name ? file.name : '';
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
};

export const handleFilePreview = async (
  callback: Promise<AxiosResponse>,
  handleSetFile: (file: { content: string; type: string }) => void,
  handleSetModalOpen: (open: boolean) => void
) => {
  const response = await callback;
  const file = response.data;
  handleSetFile({
    content: file.content,
    type: file.contentType,
  });
  handleSetModalOpen(true);
};

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const formatAmount = (amount: number, options?: Intl.NumberFormatOptions): string =>
  new Intl.NumberFormat('ru-RU', options).format(amount);

export async function logoutHandler(oidcAuth: AuthContextProps) {
  try {
    oidcAuth.removeUser();
    oidcAuth.signoutRedirect({
      id_token_hint: oidcAuth.user?.id_token,
    });
    oidcAuth.clearStaleState();
    sessionStorage.clear();
  } catch (error) {
    console.log('logoutHandler');
  }
}

export const getAccessToken = (): string | undefined => {
  const rawUser = sessionStorage.getItem(
    `oidc.user:${process.env.REACT_APP_AUTHORITY}:${process.env.REACT_APP_CLIENT_ID}`
  );
  if (rawUser) {
    const user = JSON.parse(rawUser);
    const token = user.access_token;
    return token;
  }
};

export const getOrganizationId = (): string | undefined => {
  const rawUser = sessionStorage.getItem(
    `oidc.user:${process.env.REACT_APP_AUTHORITY}:${process.env.REACT_APP_CLIENT_ID}`
  );
  if (rawUser) {
    const user = JSON.parse(rawUser);
    const organizationId = user.profile.organization_id[0];
    return organizationId;
  }
};

export function getCookie(name: string) {
  const matches = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
  );
  return matches ? decodeURIComponent(matches[1]) : undefined;
}

export const errorRequestToastHandler = (error: AxiosError<ErrorResponse>) => {
  toast.error(error.response?.data.detail, {
    position: toast.POSITION.TOP_LEFT,
  });
};

export const getUniqueCities = (
  citiesArr: ExtendedSuggestion[],
  valueArr?: OptionType<SettlementShort>[]
) => {
  let uniqueCities = citiesArr;

  valueArr &&
    valueArr.forEach((value) => {
      uniqueCities = uniqueCities.filter(
        (city) => city.suggestion.data.fias_id !== value.value.fiasId
      );
    });

  return uniqueCities;
};

export const editRuLocale = () => {
  const buildMonths = buildLocalizeFn({
    values: monthValues,
    defaultWidth: 'wide',
    formattingValues: monthValues,
    defaultFormattingWidth: 'wide',
  });

  if (ru.localize) {
    ru.localize.month = buildMonths;
  }
  return ru;
};

export const editChatRuLocale = () => {
  const buildMonths = buildLocalizeFn({
    values: chatMonthValues,
    defaultWidth: 'wide',
    formattingValues: chatMonthValues,
    defaultFormattingWidth: 'wide',
  });

  if (ru.localize) {
    ru.localize.month = buildMonths;
  }
  return ru;
};

export const viewFileSize = (size: number): string => {
  const partKb = Math.floor(size / FileSizePart.KB);
  const partMb = Math.floor(size / FileSizePart.MB);
  const partGb = Math.floor(size / FileSizePart.GB);
  if (partKb > 1 && partMb < 1) {
    return partKb + ' KB';
  } else if (partMb > 1 && partGb < 1) {
    return partMb + ' MB';
  } else if (partGb > 1) {
    return partGb + ' GB';
  } else {
    return size + ' B';
  }
};

export const getMonthByNumber = (monthNumber: number): string => {
  return monthsMap[monthNumber];
};

export const OrderTableStatuses: {
  [id: number]: { id: number; name: string; color: string; label?: string };
} = {
  1: {
    id: 1,
    name: 'Черновик',
    color: 'warning',
  },
  2: {
    id: 2,
    name: 'На рассмотрении',
    color: 'warning',
  },
  3: {
    id: 3,
    name: 'На проверке',
    color: 'warning',
  },
  4: {
    id: 4,
    name: 'На согласовании',
    color: 'warning',
  },
  5: {
    id: 5,
    name: 'На доработке',
    color: 'warning',
  },
  6: {
    id: 6,
    name: 'Отклонена',
    color: 'danger',
  },
  7: {
    id: 7,
    name: 'Согласована',
    color: 'success-60',
  },
  8: {
    id: 8,
    name: 'Оплачена',
    color: 'success-60',
  },
  9: {
    id: 9,
    name: 'На оплате',
    color: 'success-60',
  },
  10: {
    id: 10,
    name: 'Ошибка',
    color: 'danger',
  },
  11: {
    id: 11,
    name: 'Исполнена',
    color: 'success-60',
  },
  12: {
    id: 12,
    name: 'Отменена',
    color: 'success-60',
  },
  13: {
    id: 13,
    name: 'Доход не фискализирован',
    color: 'warning',
  },
};

export const isEmptyObject = (obj: object) => {
  return Object.keys(obj).length === 0;
};

export const alertBaseError = (data: AxiosError<BaseError>) => {
  const uniqueMessages = new Set();

  data.response?.data.errors
    ? Object.values(data.response?.data.errors).forEach((errors) =>
        errors.forEach((message) => {
          if (!uniqueMessages.has(message)) {
            uniqueMessages.add(message);
            toast.error(message);
          }
        })
      )
    : toast.error(data.response?.data.detail);
};

export const convertHeicToJpeg = async (heicFile: File): Promise<Blob> => {
  try {
    const jpeg = await heicTo({
      blob: heicFile,
      type: 'image/jpeg',
      quality: 0.5,
    });

    return jpeg;
  } catch (error) {
    throw new Error('Не удалось конвертировать HEIC в JPEG');
  }
};

export const addFilesToFormData = async (files: FileList): Promise<FormData> => {
  const formData: FormData = new FormData();

  for (let i = 0; i < files.length; i++) {
    const isHeic = files[i].name.toLowerCase().endsWith('.heic');
    const splitName = files[i].name.split('.');

    const blob = isHeic
      ? await convertHeicToJpeg(files[i])
      : new Blob([files[i]], { type: files[i].type });

    formData.append('files', blob, isHeic ? `${splitName[0]}.jpeg` : files[i].name);
  }

  return formData;
};

export const handleOrderReceiptView = (
  e: React.MouseEvent<HTMLAnchorElement>,
  order: ExecutorPaymentRequestDto | ClientPaymentRequestDto,
  fetchSingleFile: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<AxiosResponse<FileContentDto, any>, unknown>>
) => {
  if (
    order.status === OrderDetailsStatusEnum.Payed ||
    order.receipt?.statusName === LocalizedOrderReceiptStatusNameEnum.Offline
  ) {
    fetchSingleFile();
    e.preventDefault();
  }
};
